import { useState } from "react";
import { ArrowRight, Clock, Package } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, SERVICE_FLOW } from "@/data/partnerConfig";
import { useProjects } from "@/engine/projects";
import { Card, PageHeader, Btn, Pill, SlideOver, Field, inputCls, Segmented } from "../ui";

export default function PartnerServices() {
  const PROJECTS = useProjects();
  const [ordering, setOrdering] = useState(null);
  const [project, setProject] = useState(PROJECTS[0].ref);
  const [step, setStep] = useState(0);

  const open = (s) => { setOrdering(s); setStep(0); };

  return (
    <div data-testid="partner-services">
      <PageHeader
        eyebrow="CozyHome Central"
        title="Services CozyHome"
        sub="Un catalogue de prestations techniques professionnelles, à commander pour vos projets auprès de CozyHome Central. Vous restez concentré sur vos clients : la production technique est prise en charge."
      />

      <Card className="overflow-hidden">
        <div className="h-1 bg-cognac" />
        <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-stone-600">
            <span className="font-bold text-navy">Prestations facturées séparément.</span> Elles ne sont pas incluses dans l'abonnement Partenaire de 249 € HT / mois.
          </p>
          <Pill tone="copper">Tarifs communiqués sur devis</Pill>
        </div>
      </Card>

      <div data-testid="services-catalog" className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {SERVICES.map((s) => (
          <Card key={s.id} testid={`service-card-${s.id}`} className="flex flex-col p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-petrol-tint text-petrol"><Package className="h-5 w-5" /></span>
            <h2 className="mt-4 font-display text-xl font-medium text-navy sm:text-2xl">{s.title}</h2>
            <p className="mt-1.5 flex-1 text-sm leading-relaxed text-stone-500">{s.desc}</p>
            <div className="mt-4 flex items-center justify-between border-t border-[#F0F2F3] pt-3">
              <span className="flex items-center gap-1.5 text-xs text-stone-500"><Clock className="h-3.5 w-3.5" /> {s.lead}</span>
              <Btn variant="soft" testid={`service-order-${s.id}`} onClick={() => open(s)} className="px-3.5 py-2 text-xs">Commander <ArrowRight className="h-3.5 w-3.5" /></Btn>
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-6 p-5 sm:p-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Comment ça se passe</p>
        <ol data-testid="services-flow" className="mt-3 flex flex-wrap items-center gap-y-2">
          {SERVICE_FLOW.map((f, i) => (
            <li key={f} className="flex items-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-petrol-bg px-3 py-1.5 text-xs font-semibold text-navy">
                <span className="font-display text-sm text-petrol">{i + 1}</span> {f}
              </span>
              {i < SERVICE_FLOW.length - 1 && <ArrowRight className="mx-1 h-3 w-3 text-stone-300" />}
            </li>
          ))}
        </ol>
      </Card>

      <SlideOver open={!!ordering} onClose={() => setOrdering(null)} testid="service-order" eyebrow="Commande de service" title={ordering?.title}
        footer={
          <div className="flex justify-between gap-2">
            <Btn variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} testid="service-order-prev">Retour</Btn>
            {step < 2 ? (
              <Btn onClick={() => setStep((s) => s + 1)} testid="service-order-next">Continuer <ArrowRight className="h-4 w-4" /></Btn>
            ) : (
              <Btn variant="copper" testid="service-order-submit" onClick={() => { setOrdering(null); toast.info("La commande et le paiement auprès de CozyHome Central seront connectés dans la phase suivante."); }}>
                Valider la demande
              </Btn>
            )}
          </div>
        }
      >
        {ordering && (
          <>
            <ol className="flex flex-wrap gap-1.5" data-testid="service-order-steps">
              {SERVICE_FLOW.map((f, i) => (
                <li key={f} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${i === step ? "bg-petrol text-white" : i < step ? "bg-petrol-tint text-petrol" : "bg-[#F1F3F4] text-stone-400"}`}>{f}</li>
              ))}
            </ol>
            <div className="mt-6 space-y-4">
              {step === 0 && (
                <Field label="Projet concerné">
                  <select data-testid="service-order-project" className={inputCls} value={project} onChange={(e) => setProject(e.target.value)}>
                    {PROJECTS.map((p) => <option key={p.ref} value={p.ref}>{p.ref} — {p.name}</option>)}
                  </select>
                </Field>
              )}
              {step === 1 && (
                <>
                  <Field label="Service">
                    <Segmented testid="service-order-service" size="sm" value={ordering.id} onChange={(v) => setOrdering(SERVICES.find((s) => s.id === v))} options={SERVICES.map((s) => ({ value: s.id, label: s.title }))} />
                  </Field>
                  <p className="text-sm text-stone-500">{ordering.desc}</p>
                </>
              )}
              {step === 2 && (
                <>
                  <Field label="Informations nécessaires"><textarea rows={5} className={inputCls} placeholder="Précisez le contexte, les pièces déjà disponibles, les délais souhaités…" data-testid="service-order-notes" /></Field>
                  <div className="rounded-xl bg-[#FBF6F2] p-4 text-xs leading-relaxed text-stone-600">
                    Projet <span className="font-mono font-bold text-petrol">{project}</span> · Service <b>{ordering.title}</b>. Le tarif vous sera communiqué par CozyHome Central avant toute commande.
                    Prestation facturée séparément de l'abonnement.
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </SlideOver>
    </div>
  );
}
